import styled from '@emotion/styled';
import { Input } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { Moment } from 'moment';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import i18n from 'src/config/i18n';
import { Topics } from 'src/modules/programs/components/Topics';
import { DateTimePicker } from 'src/shared/components/DateTimePicker';
import { color, font } from 'src/shared/styles/variables';

import { ActivitiesSelectionDomain, PlannedActivity } from '../models/weekThree';
import { getPlannedActivities, getRemainingActivitiesCount } from '../selectors/weekThreeSelectors';

const SelectedActivityTitle = styled('div')({
    padding: '0.6rem 1.25rem',
    margin: '0.8rem 0.8rem 0.8rem 0',
    borderRadius: 20,
    color: color.white,
    backgroundColor: color.easternBlue,
    transition: 'all 0.2s',
    display: 'inline-block',
});

interface SelectedActivityProps extends WithTranslation {
    activityTitle: string;
    dateToBePerformed: Moment;
    onDateTimeChange: (dateTime: Moment, activityTitle: string) => void;
}

const SelectedActivity = withTranslation()(
    ({ activityTitle, dateToBePerformed, onDateTimeChange, t }: SelectedActivityProps) => (
        <div>
            <SelectedActivityTitle>{activityTitle}</SelectedActivityTitle>
            <DateTimePicker
                key={activityTitle}
                showTime={{
                    minuteStep: 5,
                    use12Hours: i18n.language === 'en',
                    format: t('inputs.dateTime.dateTimeformat'),
                }}
                format={t('inputs.dateTime.dayTimeFormat')}
                placeholder="Select date and time"
                onChange={(dateTime: Moment) => onDateTimeChange(dateTime, activityTitle)}
                defaultValue={dateToBePerformed}
            />
        </div>
    ),
);

const MainQuestion = styled('h3')(
    {
        color: color.chathamsBlue,
        ':not(:first-child)': {
            marginTop: '3rem',
        },
    },
    font.heavy(font.size.mediumExtended),
);

const ActivityCounter = styled('span')(
    {
        color: color.chathamsBlue,
        ':not(:first-child)': {
            marginTop: '3rem',
        },
    },
    font.heavy(font.size.mediumExtended),
);

const StyledInput = styled<any>(Input)({
    marginTop: '1.8rem',
});

const EmptyActivities = styled('div')({
    padding: '0.6rem 1.25rem',
    borderRadius: 20,
    color: color.mamba,
    backgroundColor: color.white,
    border: `solid 1px ${color.silver}`,
    display: 'inline-block',
});

interface StateProps {
    remainingActivitiesCount: number;
    plannedActivities: PlannedActivity[];
}

interface Props extends StateProps {
    selectedDomain: ActivitiesSelectionDomain;
    activities: string[];
    onActivityChange: (domainTitle: string, activityTitle: string) => void;
    onInputChange: (
        domainTitle: string,
        activityTitle: string,
        value: string | Moment,
        field: string,
    ) => void;
}

class ActivitiesSelectionDomainTabContainer extends React.PureComponent<Props> {
    public handleAdditionalActivityTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
        this.props.onInputChange(
            this.props.selectedDomain.title,
            'Other',
            e.target.value,
            'additionalActivityTitle',
        );

    public handleActivityChange = (action: string) => {
        const {
            selectedDomain,
            onActivityChange,
            remainingActivitiesCount,
            plannedActivities,
        } = this.props;

        const areActivitiesRemaining =
            remainingActivitiesCount > 0 ||
            plannedActivities.map(activity => activity.title).includes(action);

        if (areActivitiesRemaining) {
            onActivityChange(selectedDomain.title, action);
        }
    };

    public handleActivityDateTimeChange = (dateTime: Moment, actionTitle: string) => {
        this.props.onInputChange(
            this.props.selectedDomain.title,
            actionTitle,
            dateTime,
            'dateToBePerformed',
        );
    };

    public render(): JSX.Element | null {
        const {
            selectedDomain,
            remainingActivitiesCount,
            activities,
            plannedActivities,
        } = this.props;

        const selectedDomainActivities = plannedActivities.filter(
            activity => activity.domainId === selectedDomain.id,
        );

        const activitiesTitles = selectedDomainActivities.map(activity => activity.title);

        const isOtherActivitySelected = activitiesTitles.includes('Other');

        return (
            <>
                <MainQuestion>
                    Choose {selectedDomain.title} action{' '}
                    {remainingActivitiesCount >= 0 && (
                        <ActivityCounter>
                            {remainingActivitiesCount > 0
                                ? `(select ${remainingActivitiesCount} more):`
                                : '(maximum of 3 actions selected)'}
                        </ActivityCounter>
                    )}
                </MainQuestion>
                <Topics
                    topics={activities.reverse()}
                    selectedTopics={activitiesTitles}
                    onTopicChange={this.handleActivityChange}
                />
                {isOtherActivitySelected && (
                    <StyledInput
                        onChange={this.handleAdditionalActivityTitleChange}
                        size="large"
                        placeholder="Please provide your activity"
                    />
                )}
                <MainQuestion>Schedule selected action:</MainQuestion>
                {!isEmpty(selectedDomainActivities) ? (
                    selectedDomainActivities.map(activity => (
                        <SelectedActivity
                            key={activity.title}
                            activityTitle={
                                activity.title === 'Other'
                                    ? activity.additionalTitle || 'Other'
                                    : activity.title
                            }
                            dateToBePerformed={activity.dateToBePerformed}
                            onDateTimeChange={this.handleActivityDateTimeChange}
                        />
                    ))
                ) : (
                    <EmptyActivities>No actions selected</EmptyActivities>
                )}
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    plannedActivities: getPlannedActivities(state),
    remainingActivitiesCount: getRemainingActivitiesCount(state),
});

export const ActivitiesSelectionDomainTab = connect(mapStateToProps)(
    ActivitiesSelectionDomainTabContainer,
);
